import { useState, useEffect, useCallback, useMemo } from 'react'
import { debounce } from '@/utilities/debounce'
import { GridColDef } from '@mui/x-data-grid'

/**
 * Props for useColumnsStorage hook
 */
export interface UseColumnsStorageProps {
  /**
   * Array of all available columns or column groups
   */
  columns: any[] | Record<string, any[]>
  /**
   * Key for storing selected columns in localStorage
   */
  storageKey: string
  /**
   * Initial columns to select if nothing is stored in localStorage
   */
  initialColumns?: string[]
  /**
   * Fields introduced after this storageKey first shipped. Opt-in: when omitted,
   * the hook behaves byte-for-byte as it did before.
   */
  newColumns?: string[]
}

/**
 * Return type for useColumnsStorage hook
 */
export interface UseColumnsStorageReturn {
  /**
   * Currently selected column fields
   */
  selectedColumns: string[]
  /**
   * Default selected column fields from storage or initialization
   */
  defaultSelectedColumns: string[]
  /**
   * Filtered columns based on selection
   */
  filteredColumns: GridColDef[]
  /**
   * Handler for column selection change
   */
  handleChangeColumns: (columns: string[]) => void
  /**
   * Set selected columns directly
   */
  setSelectedColumns: React.Dispatch<React.SetStateAction<string[]>>
}

/**
 * Hook for managing column selection with localStorage persistence
 */
export const useColumnsStorage = ({
  columns,
  storageKey,
  initialColumns,
  newColumns
}: UseColumnsStorageProps): UseColumnsStorageReturn => {
  // States for selected and default columns
  const [defaultSelectedColumns, setDefaultSelectedColumns] = useState<
    string[]
  >([])
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])

  // Initialize default selected columns from localStorage or use all columns if not found
  const initDefaultSelectedColumns = useCallback(() => {
    const storedColumns = localStorage.getItem(storageKey)

    if (storedColumns && storedColumns !== '[]') {
      const parsedColumns = JSON.parse(storedColumns)

      if (newColumns?.length) {
        // Fields in `newColumns` are merged into an existing selection once,
        // then recorded here so hiding one afterwards is permanent.
        const seenKey = `${storageKey}_seen`
        const seen: string[] = JSON.parse(localStorage.getItem(seenKey) ?? '[]')
        const unseenNew = newColumns.filter(
          field => !seen.includes(field) && !parsedColumns.includes(field)
        )
        const selection = unseenNew.length
          ? [...parsedColumns, ...unseenNew]
          : parsedColumns

        setDefaultSelectedColumns(selection)
        setSelectedColumns(selection)

        if (unseenNew.length) {
          localStorage.setItem(storageKey, JSON.stringify(selection))
        }
        localStorage.setItem(
          seenKey,
          JSON.stringify([...new Set([...seen, ...newColumns])])
        )
      } else {
        setDefaultSelectedColumns(parsedColumns)
        setSelectedColumns(parsedColumns)
      }
    } else {
      // Use initialColumns if provided, otherwise extract from columns
      const defaultColumns =
        initialColumns ||
        (Array.isArray(columns)
          ? columns.map(item => item.field)
          : Object.values(columns)
              .flat()
              .map(item => item.field))

      setDefaultSelectedColumns(defaultColumns)
      setSelectedColumns(defaultColumns)

      // Save to localStorage
      localStorage.setItem(storageKey, JSON.stringify(defaultColumns))

      if (newColumns?.length) {
        // The defaults already contain the new columns, so they must not be
        // offered again on a later load.
        const seenKey = `${storageKey}_seen`
        const seen: string[] = JSON.parse(localStorage.getItem(seenKey) ?? '[]')
        localStorage.setItem(
          seenKey,
          JSON.stringify([...new Set([...seen, ...newColumns])])
        )
      }
    }
  }, [columns, storageKey, initialColumns, newColumns])

  // Debounced function to save columns to localStorage
  const saveColumnsToLocalStorage = useMemo(
    () =>
      debounce((columns: string[]) => {
        localStorage.setItem(storageKey, JSON.stringify(columns))
      }, 2000),
    [storageKey]
  )

  // Handle column selection change
  const handleChangeColumns = useCallback(
    (columns: string[]) => {
      setSelectedColumns(columns)
      saveColumnsToLocalStorage(columns)
    },
    [saveColumnsToLocalStorage]
  )

  // Filter available columns based on selected columns
  const filteredColumns = useMemo(() => {
    if (Array.isArray(columns)) {
      return columns.filter(item => selectedColumns.includes(item.field))
    } else {
      // For grouped columns
      return Object.values(columns)
        .flat()
        .filter(item => selectedColumns.includes(item.field))
    }
  }, [columns, selectedColumns])

  // Initialize on mount
  useEffect(() => {
    initDefaultSelectedColumns()
  }, [initDefaultSelectedColumns])

  return {
    selectedColumns,
    defaultSelectedColumns,
    filteredColumns,
    handleChangeColumns,
    setSelectedColumns
  }
}

export default useColumnsStorage
